import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Landmark,
  Search,
  Users,
  FileText,
  ShieldCheck,
  Sparkles,
  ChevronDown,
  ListChecks,
  CircleHelp,
  BellRing,
  AlarmClock,
  Play,
  Trash2,
  ArrowUpRight,
  LogIn,
  Radar,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Button, Card, EmptyState, Pill, cn } from '../components/ui';
import { api } from '../lib/api';
import {
  COTEJO_LABEL,
  COTEJO_TONE,
  RP_EJEMPLO_FOLIO,
  RP_FAQ,
  SUBSECTOR_LABEL,
  VIGENCIA_LABEL,
  VIGENCIA_TONE,
  rpComoUsar,
  rpInterpretar,
  type HelperBlock,
  type RpConsultaInput,
  type RpConsultaResult,
  type RpSeccion,
  type RpWatch,
  type RpWeeklyCheckResult,
  type RpLoginInput,
  type RpSweepResult,
  type RpSweepRow,
} from '../lib/registro';

/** Inline **bold** → <strong>. Keeps the deterministic helper copy readable. */
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
        const m = /^\*\*([^*]+)\*\*$/.exec(part);
        return m ? (
          <strong key={i} className="font-semibold text-ink">
            {m[1]}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </>
  );
}

/**
 * Cuerpo de «Vigilancia Registral» — vive como pestaña dentro de Alertas y Casos.
 * Consulta/vigila folios del Registro Público y barre la cartera. `onGoToValidacion`
 * cambia a la pestaña de Validación masiva sin salir de la pantalla.
 */
export function VigilanciaRegistralPanel({
  onGoToValidacion,
}: {
  onGoToValidacion: () => void;
}) {
  const qc = useQueryClient();
  const [seccion, setSeccion] = useState<RpSeccion>('mercantil');
  const [folio, setFolio] = useState('');
  const [nombre, setNombre] = useState('');

  const consulta = useMutation<RpConsultaResult, Error, RpConsultaInput>({
    mutationFn: (input) => api.rpConsulta(input),
  });

  const watchlist = useQuery({ queryKey: ['rp-watch'], queryFn: () => api.rpWatchList() });
  const watchAdd = useMutation({
    mutationFn: (input: RpConsultaInput) => api.rpWatchAdd(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rp-watch'] }),
  });
  const watchRemove = useMutation({
    mutationFn: (id: string) => api.rpWatchRemove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rp-watch'] }),
  });
  // El chequeo semanal genera alertas → refrescamos la bandeja de Alertas también.
  const runCheck = useMutation({
    mutationFn: () => api.rpRunWeeklyCheck(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rp-watch'] });
      void qc.invalidateQueries({ queryKey: ['alertas'] });
    },
  });

  const puedeConsultar = folio.trim().length > 0 || nombre.trim().length > 0;

  function run() {
    if (!puedeConsultar) return;
    const input: RpConsultaInput = {
      seccion,
      folio: folio.trim(),
      ...(nombre.trim() ? { nombre: nombre.trim() } : {}),
    };
    consulta.mutate(input);
  }

  const result = consulta.data ?? null;
  const watched = watchlist.data ?? [];
  const isWatched = result
    ? watched.some((w) => w.folio === result.folio && w.seccion === result.seccion)
    : false;

  return (
    <div>
      {/* Login del Registro Público + barrido de toda la cartera. */}
      <BarrerCartera />

      <div className="grid gap-6 lg:grid-cols-3 [&>*]:min-w-0">
        {/* Columna principal: formulario + resultado */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <div className="grid gap-4 sm:grid-cols-[150px_1fr] sm:items-center">
              <label htmlFor="rp-tipo" className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
                Tipo
              </label>
              <select
                id="rp-tipo"
                value={seccion}
                onChange={(e) => setSeccion(e.target.value as RpSeccion)}
                className="w-full rounded-lg border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="mercantil">Mercantil (sociedades / fundaciones)</option>
                <option value="personas">Personas</option>
              </select>

              <label htmlFor="rp-folio" className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
                Folio
              </label>
              <input
                id="rp-folio"
                value={folio}
                onChange={(e) => setFolio(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && run()}
                inputMode="numeric"
                placeholder="Número de folio"
                className="w-full rounded-lg border border-line-strong bg-canvas px-3 py-2 font-mono text-sm text-ink placeholder:text-ink-faint focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30"
              />

              <label htmlFor="rp-nombre" className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
                Nombre <span className="font-normal normal-case text-ink-faint">(opcional)</span>
              </label>
              <input
                id="rp-nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && run()}
                placeholder="Denominación exacta o parcial"
                className="w-full rounded-lg border border-line-strong bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button onClick={run} disabled={!puedeConsultar || consulta.isPending}>
                <Search className="h-4 w-4" strokeWidth={2} />
                {consulta.isPending ? 'Consultando el Registro Público…' : 'Consultar'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSeccion('mercantil');
                  setFolio(RP_EJEMPLO_FOLIO);
                  setNombre('');
                }}
                disabled={consulta.isPending}
              >
                Ver ejemplo
              </Button>
              <span className="text-xs text-ink-faint">
                La consulta abre el folio en el sitio y descarga los PDFs de Prelación.
              </span>
            </div>
          </Card>

          {consulta.isError && (
            <Card>
              <p className="text-sm font-medium text-tone-red">
                No se pudo completar la consulta. Verifica el folio e inténtalo de nuevo.
              </p>
            </Card>
          )}

          {result ? (
            <ResultView
              result={result}
              watched={isWatched}
              watching={watchAdd.isPending}
              onWatch={() => watchAdd.mutate({ seccion: result.seccion, folio: result.folio })}
            />
          ) : (
            !consulta.isPending && (
              <Card>
                <EmptyState
                  icon={<Landmark className="h-8 w-8" strokeWidth={1.5} />}
                  title="Sin consulta todavía"
                  hint="Ingresa un folio (o usa «Ver ejemplo») y presiona «Consultar» para obtener el informe verificado del Registro Público."
                />
              </Card>
            )
          )}

          <VigilanciaCard
            watched={watched}
            loading={watchlist.isLoading}
            onRemove={(id) => watchRemove.mutate(id)}
            removing={watchRemove.isPending}
            onRun={() => runCheck.mutate()}
            running={runCheck.isPending}
            lastRun={runCheck.data ?? null}
          />
        </div>

        {/* Columna lateral: asistente DETERMINISTA de la herramienta */}
        <div className="lg:col-span-1">
          <HelperPanel result={result} onGoToValidacion={onGoToValidacion} />
        </div>
      </div>
    </div>
  );
}

function ResultView({
  result,
  watched,
  watching,
  onWatch,
}: {
  result: RpConsultaResult;
  watched: boolean;
  watching: boolean;
  onWatch: () => void;
}) {
  const pendientes = result.prelacion.filter((p) => !p.entregado).length;
  return (
    <div className="space-y-6">
      {/* Cabecera del informe */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-serif text-xl font-medium text-ink">{result.denominacion}</h2>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-ink-faint">
              Folio {result.folio} · {result.seccion} · consultado {result.consultadoEn}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Pill tone={VIGENCIA_TONE[result.vigencia]}>{VIGENCIA_LABEL[result.vigencia]}</Pill>
            {watched ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary-dark">
                <BellRing className="h-3.5 w-3.5" strokeWidth={2} /> En vigilancia
              </span>
            ) : (
              <Button variant="outline" onClick={onWatch} disabled={watching} className="px-3 py-1.5 text-xs">
                <BellRing className="h-3.5 w-3.5" strokeWidth={2} />
                {watching ? 'Agregando…' : 'Vigilar este folio'}
              </Button>
            )}
          </div>
        </div>
        <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Field label="Estado (texto del registro)" value={result.estadoRaw} />
          <Field label="Tipo de organización" value={result.tipoOrganizacion} />
          <Field label="Fecha de inscripción" value={result.fechaInscripcion} />
          <Field label="Domicilio" value={result.domicilio} />
        </dl>
        {result.demo && (
          <p className="mt-4 rounded-lg bg-canvas px-3 py-2 text-xs text-ink-faint">
            Resultado de <strong>demostración</strong> (datos ficticios). En producción, la consulta se ejecuta
            en vivo contra el Registro Público de Panamá.
          </p>
        )}
      </Card>

      {/* Miembros */}
      <Card className="p-0">
        <SectionTitle icon={<Users className="h-4 w-4" strokeWidth={2} />} title="Miembros relacionados" count={result.miembros.length} />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                {['Cargo', 'Miembro'].map((h) => (
                  <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.miembros.map((m, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  <td className="px-5 py-2.5 align-top font-semibold text-ink">{m.cargo}</td>
                  <td className="px-5 py-2.5 align-top text-ink-soft">{m.nombre}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-line px-5 py-3 text-xs text-ink-faint">
          Los miembros del <em>servicio de directores nominales</em> aparecen aquí; cotéjalos contra listas y PEP.
        </p>
      </Card>

      {/* Prelación */}
      <Card className="p-0">
        <SectionTitle icon={<FileText className="h-4 w-4" strokeWidth={2} />} title="Prelación (trámites)" count={result.prelacion.length} />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[42rem] text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                {['Entrada', 'Trámite', 'Estado', 'Entregado'].map((h) => (
                  <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.prelacion.map((p, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  <td className="px-5 py-2.5 align-top font-mono text-xs text-ink">{p.entrada}</td>
                  <td className="px-5 py-2.5 align-top text-ink-soft">{p.tramite}</td>
                  <td className="px-5 py-2.5 align-top text-ink-soft">{p.estado}</td>
                  <td className="px-5 py-2.5 align-top">
                    <Pill tone={p.entregado ? 'green' : 'amber'}>{p.entregado ? 'Sí' : 'Revisar'}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pendientes > 0 && (
          <p className="border-t border-line px-5 py-3 text-xs text-tone-amber">
            {pendientes} entrada(s) sin la marca «entregado» — verifica que el trámite esté en orden.
          </p>
        )}
      </Card>

      {/* Cotejo */}
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary-dark">
            <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-serif text-sm font-medium text-ink">Cotejo Miembros ↔ Prelación</div>
            <p className="mt-0.5 text-xs text-ink-soft">{result.cotejo.detalle}</p>
          </div>
          <Pill tone={COTEJO_TONE[result.cotejo.resultado]}>{COTEJO_LABEL[result.cotejo.resultado]}</Pill>
        </div>
      </Card>

      <p className="text-xs text-ink-faint">Fuente: {result.fuente}</p>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-wider text-ink-faint">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

function SectionTitle({ icon, title, count }: { icon: ReactNode; title: string; count: number }) {
  return (
    <div className="flex items-center gap-2 border-b border-line px-5 py-3.5">
      <span className="text-primary-dark">{icon}</span>
      <h3 className="font-serif text-sm font-medium text-ink">{title}</h3>
      <span className="ml-auto rounded-full bg-canvas px-2 py-0.5 font-mono text-[10px] text-ink-faint">{count}</span>
    </div>
  );
}

/**
 * Asistente DETERMINISTA de la herramienta: guía de uso cuando no hay resultado,
 * e interpretación reglada del resultado cuando lo hay. Sin LLM — todo el texto
 * se deriva de los datos y de un catálogo de preguntas frecuentes.
 */
function HelperPanel({
  result,
  onGoToValidacion,
}: {
  result: RpConsultaResult | null;
  onGoToValidacion: () => void;
}) {
  const blocks: HelperBlock[] = result ? rpInterpretar(result) : [rpComoUsar()];
  return (
    // Se fija bajo la barra de navegación y nunca se corta: si el contenido supera
    // la altura visible, hace scroll interno (no queda tapado por el pie de página).
    <div className="lg:sticky lg:top-28 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:pr-1">
      <Card className="border-primary/20 bg-panel">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary-dark">
            <Sparkles className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <div className="flex-1">
            <div className="font-serif text-base font-medium leading-none text-ink">Asistente de la herramienta</div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
              Guía determinista · sin IA generativa
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {blocks.map((b, i) => (
            <div key={i}>
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-primary-dark">
                {result ? <ListChecks className="h-3.5 w-3.5" strokeWidth={2.25} /> : null}
                {b.title}
              </div>
              <ul className="space-y-1.5 text-sm text-ink-soft">
                {b.lines.map((line, j) => (
                  <li key={j} className="leading-relaxed">
                    <Rich text={line} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-5 border-t border-line pt-4">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink-soft">
            <CircleHelp className="h-3.5 w-3.5" strokeWidth={2.25} />
            Notas útiles — cuándo actuar y límites
          </div>
          <div className="space-y-1.5">
            {RP_FAQ.map((f) => (
              <Faq key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>

        {result && (
          <button
            type="button"
            onClick={onGoToValidacion}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-line-strong bg-canvas px-3 py-2.5 text-xs font-semibold text-primary-dark transition-colors hover:border-primary"
          >
            <ListChecks className="h-4 w-4" strokeWidth={2} />
            Cotejar miembros en Validación masiva
          </button>
        )}
      </Card>
    </div>
  );
}

function Faq({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-line bg-canvas">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-semibold text-ink"
      >
        {q}
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-ink-faint transition-transform', open && 'rotate-180')} strokeWidth={2.25} />
      </button>
      {open && (
        <p className="border-t border-line px-3 py-2 text-xs leading-relaxed text-ink-soft">
          <Rich text={a} />
        </p>
      )}
    </div>
  );
}

/** dd mmm de una fecha ISO (es-PA). */
function fechaCorta(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-PA', { day: '2-digit', month: 'short' });
}

const prioridadTone = (p: 'Alta' | 'Media' | 'Baja'): 'red' | 'amber' | 'slate' =>
  p === 'Alta' ? 'red' : p === 'Media' ? 'amber' : 'slate';

/**
 * Vigilancia semanal: la parte de "alarma". Lista los folios marcados, corre el
 * chequeo (que genera alertas en Alertas y Casos identificadas como «Vigilancia
 * Registral · <Subsector>») y muestra el resumen de la última corrida.
 */
function VigilanciaCard({
  watched,
  loading,
  onRemove,
  removing,
  onRun,
  running,
  lastRun,
}: {
  watched: RpWatch[];
  loading: boolean;
  onRemove: (id: string) => void;
  removing: boolean;
  onRun: () => void;
  running: boolean;
  lastRun: RpWeeklyCheckResult | null;
}) {
  return (
    <Card className="p-0">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3.5">
        <span className="text-primary-dark">
          <AlarmClock className="h-4 w-4" strokeWidth={2} />
        </span>
        <h3 className="font-serif text-sm font-medium text-ink">Vigilancia semanal</h3>
        <span className="ml-auto rounded-full bg-canvas px-2 py-0.5 font-mono text-[10px] text-ink-faint">
          {watched.length}
        </span>
      </div>

      <div className="px-5 py-4">
        <p className="text-xs text-ink-soft">
          Marca un folio con <strong>Vigilar este folio</strong>. Cada semana se vuelve a consultar y, ante un
          cambio (vigencia, fusión, trámites sin entregar…), se genera una alerta en{' '}
          <Link to="/alertas" className="font-semibold text-primary-dark hover:underline">
            Alertas y Casos
          </Link>{' '}
          identificada como «Vigilancia Registral» con su subsector.
        </p>

        {loading ? (
          <p className="mt-4 text-sm text-ink-faint">Cargando…</p>
        ) : watched.length === 0 ? (
          <div className="mt-4 rounded-xl border border-dashed border-line-strong bg-canvas px-4 py-6 text-center">
            <BellRing className="mx-auto h-6 w-6 text-ink-faint" strokeWidth={1.5} />
            <p className="mt-2 text-sm text-ink-soft">Sin folios en vigilancia.</p>
            <p className="text-xs text-ink-faint">Consulta un folio arriba y presiona «Vigilar este folio».</p>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[32rem] text-sm">
              <thead>
                <tr className="border-b border-line text-left">
                  {['Folio', 'Denominación', 'Próx. chequeo', 'Hallazgos', ''].map((h) => (
                    <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {watched.map((w) => (
                  <tr key={w.id} className="border-b border-line last:border-0">
                    <td className="px-3 py-2.5 align-top font-mono text-xs text-ink">{w.folio}</td>
                    <td className="px-3 py-2.5 align-top text-ink-soft">{w.denominacion}</td>
                    <td className="px-3 py-2.5 align-top text-ink-soft">{fechaCorta(w.nextCheckAt)}</td>
                    <td className="px-3 py-2.5 align-top">
                      {w.hallazgos > 0 ? (
                        <Pill tone="amber">{w.hallazgos}</Pill>
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-top text-right">
                      <button
                        onClick={() => onRemove(w.id)}
                        disabled={removing}
                        aria-label="Quitar de vigilancia"
                        className="text-ink-faint transition-colors hover:text-tone-red disabled:opacity-40"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={onRun} disabled={running || watched.length === 0}>
            <Play className="h-4 w-4" strokeWidth={2} />
            {running ? 'Revisando…' : 'Ejecutar chequeo ahora'}
          </Button>
          <span className="text-xs text-ink-faint">Simula la corrida semanal sobre los folios en vigilancia.</span>
        </div>

        {lastRun && (
          <div className="mt-4 rounded-xl border border-line bg-canvas p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-ink">
                {lastRun.revisados} folio(s) revisados · {lastRun.nuevasAlertas} alerta(s) nueva(s)
              </span>
              {lastRun.nuevasAlertas > 0 && (
                <Link
                  to="/alertas"
                  className="inline-flex items-center gap-0.5 text-xs font-semibold text-primary-dark hover:underline"
                >
                  Ver en Alertas y Casos <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                </Link>
              )}
            </div>
            {lastRun.alertas.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {lastRun.alertas.map((a) => (
                  <li key={a.alertId} className="flex items-start gap-2 text-xs">
                    <Pill tone={prioridadTone(a.prioridad)}>{SUBSECTOR_LABEL[a.subsector]}</Pill>
                    <span className="text-ink-soft">
                      <span className="font-mono text-ink">{a.folio}</span> · {a.detalle}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-xs text-ink-faint">
                Sin novedades esta corrida — los folios vigilados siguen en orden (o sus hallazgos ya se
                reportaron antes).
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Login del Registro Público + «Barrer mi cartera» (barrido registral de toda la
// cartera de clientes). El usuario digita SUS credenciales del RP; el navegador
// solo las reenvía en el login y nunca las guarda (I6/I7). El barrido lo ejecuta
// el servicio: abre el folio de cada expediente, detecta cambios y levanta
// alertas (que aparecen en la pestaña de Alertas y Casos).
// ---------------------------------------------------------------------------

const INPUT_CLS =
  'w-full rounded-lg border border-line-strong bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30';

/** Estado por fila del barrido: error → cambio con alerta → cambio → sin cambios. */
function SweepRowStatus({ row }: { row: RpSweepRow }) {
  if (row.error) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <XCircle className="h-4 w-4 text-tone-red" strokeWidth={2} />
        <Pill tone="red">Error</Pill>
      </span>
    );
  }
  if (row.alerta) return <Pill tone="amber">Alerta creada</Pill>;
  if (row.cambio) return <Pill tone="amber">Cambio detectado</Pill>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <CheckCircle2 className="h-4 w-4 text-tone-green" strokeWidth={2} />
      <Pill tone="green">Sin cambios</Pill>
    </span>
  );
}

function BarrerCartera() {
  const qc = useQueryClient();
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [twocaptcha, setTwocaptcha] = useState('');
  const [sitekey, setSitekey] = useState('');
  const [avanzado, setAvanzado] = useState(false);
  // "Cambiar cuenta" debe mostrar el formulario aunque rp-salud reporte sesión guardada.
  const [forceLoginForm, setForceLoginForm] = useState(false);

  const salud = useQuery({ queryKey: ['rp-salud'], queryFn: () => api.rpSalud(), retry: false });
  const beta = salud.data?.beta;

  const login = useMutation<{ ok: boolean; sesion_guardada?: boolean }, Error, RpLoginInput>({
    mutationFn: (input) => api.rpLogin(input),
    onSuccess: (d) => { if (d.ok) setForceLoginForm(false); },
  });
  // El barrido levanta alertas → refrescamos la bandeja de Alertas y Casos.
  const sweep = useMutation<RpSweepResult, Error, void>({
    mutationFn: () => api.rpSweep(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['alertas'] }),
  });

  const sesionActiva = !forceLoginForm && (login.data?.ok === true || salud.data?.sesion_guardada === true);
  const res = sweep.data ?? null;

  function hacerLogin() {
    if (!usuario.trim() || !password.trim()) return;
    login.mutate({
      usuario: usuario.trim(),
      password,
      ...(twocaptcha.trim() ? { twocaptchaApiKey: twocaptcha.trim() } : {}),
      ...(sitekey.trim() ? { recaptchaSitekey: sitekey.trim() } : {}),
    });
  }

  return (
    <Card className="mb-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
          <Radar className="h-4 w-4 text-primary" strokeWidth={2} /> Barrer mi cartera
        </h2>
        {sesionActiva && <Pill tone="green">Sesión activa</Pill>}
      </div>

      {/* Paso 1 — acceso. Compacto; colapsa a una línea cuando la sesión está activa. */}
      {sesionActiva ? (
        <p className="mb-4 text-xs text-ink-faint">
          Conectado al Registro Público.{' '}
          <button type="button" onClick={() => { login.reset(); setForceLoginForm(true); }} className="font-semibold text-primary hover:underline">
            Cambiar cuenta
          </button>
        </p>
      ) : (
        <div className="mb-4 space-y-2">
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <input
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              placeholder="Usuario del Registro Público"
              aria-label="Usuario del Registro Público"
              autoComplete="off"
              className={INPUT_CLS}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && hacerLogin()}
              placeholder="Contraseña"
              aria-label="Contraseña"
              autoComplete="off"
              className={INPUT_CLS}
            />
            <Button onClick={hacerLogin} disabled={!usuario.trim() || !password.trim() || login.isPending}>
              <LogIn className="h-4 w-4" strokeWidth={2} />
              {login.isPending ? 'Entrando…' : 'Entrar'}
            </Button>
          </div>
          {beta?.requiere_2captcha && (
            <button
              type="button"
              onClick={() => setAvanzado((v) => !v)}
              className="flex items-center gap-1 text-xs font-medium text-ink-faint hover:text-ink"
            >
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', avanzado && 'rotate-180')} />
              Clave 2Captcha (opcional)
            </button>
          )}
          {avanzado && (
            <div className="grid gap-2 sm:grid-cols-2">
              <input value={twocaptcha} onChange={(e) => setTwocaptcha(e.target.value)} placeholder="Clave API 2Captcha" aria-label="Clave API 2Captcha" autoComplete="off" className={INPUT_CLS} />
              <input value={sitekey} onChange={(e) => setSitekey(e.target.value)} placeholder="reCAPTCHA sitekey" aria-label="reCAPTCHA sitekey" autoComplete="off" className={INPUT_CLS} />
            </div>
          )}
          <p className="text-[11px] text-ink-faint">Sus credenciales solo se usan para iniciar sesión; no se guardan.</p>
          {login.isError && <p className="text-xs font-medium text-tone-red">{login.error.message}</p>}
        </div>
      )}

      {/* Paso 2 — barrer toda la cartera: abre el folio de cada expediente y levanta
          una alerta ante cada cambio. */}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => sweep.mutate()} disabled={sweep.isPending}>
          <Radar className="h-4 w-4" strokeWidth={2} />
          {sweep.isPending ? 'Barriendo la cartera…' : 'Barrer mi cartera'}
        </Button>
        <span className="text-xs text-ink-faint">
          Consulta el folio registral de cada cliente y abre una alerta ante cada cambio (vigencia, fusión, prelación).
        </span>
      </div>
      {sweep.isError && <p className="mt-2 text-xs font-medium text-tone-red">{sweep.error.message}</p>}

      {res && (
        <div className="mt-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Pill tone="slate">{res.revisados} revisados</Pill>
            <Pill tone={res.cambios > 0 ? 'amber' : 'green'}>{res.cambios} con cambio</Pill>
            {res.errores > 0 && <Pill tone="red">{res.errores} con error</Pill>}
            {res.sinFolio > 0 && <Pill tone="slate">{res.sinFolio} sin folio</Pill>}
            {res.cambios > 0 && (
              <Link
                to="/alertas"
                className="inline-flex items-center gap-0.5 text-xs font-semibold text-primary-dark hover:underline"
              >
                Ver en Alertas y Casos <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              </Link>
            )}
          </div>

          {res.resultados.length === 0 ? (
            <p className="rounded-xl border border-dashed border-line-strong bg-canvas px-4 py-6 text-center text-sm text-ink-soft">
              Ningún cliente de la cartera tiene folio registral para barrer.
            </p>
          ) : (
            <>
              {/* mobile: tarjetas apiladas */}
              <div className="grid gap-2.5 sm:hidden">
                {res.resultados.map((r) => (
                  <div key={r.expedienteId} className="rounded-2xl border border-line bg-panel p-4 shadow-card">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-bold text-ink">{r.nombre}</span>
                      <SweepRowStatus row={r} />
                    </div>
                    <div className="mt-1 font-mono text-[11px] text-ink-faint">
                      Folio {r.folio} · {r.estado}
                    </div>
                    {r.error && <div className="mt-1 text-xs text-tone-red">{r.error}</div>}
                  </div>
                ))}
              </div>

              {/* desktop: tabla */}
              <Card className="hidden overflow-x-auto p-0 sm:block">
                <table className="w-full min-w-[40rem] text-sm">
                  <thead>
                    <tr className="border-b border-line text-left">
                      {['Cliente', 'Folio', 'Estado en el Registro', 'Resultado'].map((h) => (
                        <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {res.resultados.map((r) => (
                      <tr key={r.expedienteId} className="border-b border-line last:border-0">
                        <td className="px-5 py-3 font-bold align-top">{r.nombre}</td>
                        <td className="px-5 py-3 align-top font-mono text-[11px] text-ink-faint">{r.folio}</td>
                        <td className="px-5 py-3 align-top text-ink-soft">
                          {r.error ? <span className="text-tone-red">{r.error}</span> : r.estado}
                        </td>
                        <td className="px-5 py-3 align-top">
                          <SweepRowStatus row={r} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </>
          )}
        </div>
      )}

      {beta && !beta.scrape_disponible && (
        <p className="mt-3 flex items-start gap-1.5 text-[11px] text-ink-faint">
          <Play className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tone-amber" strokeWidth={2} />
          Beta: el scraping en vivo aún no está disponible en este entorno; el barrido usa datos de demostración.
        </p>
      )}
    </Card>
  );
}
